<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  importItem: { type: Object, required: true },
});

const emit = defineEmits(['edit', 'delete', 'sync']);

const { t } = useI18n();
const isSyncing = ref(false);

const statusColor = computed(() => {
  switch (props.importItem.status) {
    case 'active':
      return 'text-g-400';
    case 'paused':
      return 'text-y-500';
    case 'error':
      return 'text-r-500';
    default:
      return 'text-n-slate-11';
  }
});

const statusLabel = computed(() => {
  const key = `INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.STATUS.${props.importItem.status?.toUpperCase() || 'ACTIVE'}`;
  return t(key);
});

const handleSync = async () => {
  isSyncing.value = true;
  emit('sync', props.importItem.id);
  setTimeout(() => {
    isSyncing.value = false;
  }, 3000);
};
</script>

<template>
  <div
    class="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 p-4 border border-n-weak rounded-lg bg-n-alpha-2"
  >
    <div class="flex-1 min-w-0">
      <div class="flex items-center gap-2">
        <h4 class="text-sm font-medium text-n-slate-12 truncate">
          {{ importItem.name }}
        </h4>
        <span
          class="text-xs font-medium px-1.5 py-0.5 rounded-full"
          :class="statusColor"
        >
          {{ statusLabel }}
        </span>
      </div>
      <p class="text-xs text-n-slate-11 mt-0.5 truncate">
        {{ importItem.spreadsheet_id }} · {{ importItem.sheet_name || 'Sheet1' }}
      </p>
      <p class="text-xs text-n-slate-10 mt-0.5 truncate">
        → {{ importItem.api_url }}
      </p>
      <div class="flex items-center gap-3 mt-1">
        <p v-if="importItem.sync_interval" class="text-xs text-b-500">
          ⏱ Auto: cada {{ importItem.sync_interval }} min
        </p>
        <p v-else class="text-xs text-n-slate-10">
          ⏱ Manual
        </p>
        <p class="text-xs text-n-slate-10">
          · Fila: {{ importItem.last_processed_row || 0 }}
        </p>
      </div>
      <p
        v-if="importItem.error_log"
        class="text-xs text-r-500 mt-1 truncate"
      >
        {{ importItem.error_log }}
      </p>
    </div>
    <div class="flex items-center gap-2 flex-shrink-0">
      <Button
        xs
        faded
        blue
        :label="t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.ACTIONS.SYNC')"
        :is-loading="isSyncing"
        icon="i-lucide-refresh-cw"
        @click="handleSync"
      />
      <Button
        xs
        faded
        slate
        :label="t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.ACTIONS.EDIT')"
        icon="i-lucide-pencil"
        @click="emit('edit', importItem)"
      />
      <Button
        xs
        faded
        ruby
        :label="t('INTEGRATION_SETTINGS.GOOGLE_SHEET_IMPORTS.ACTIONS.DELETE')"
        icon="i-lucide-trash-2"
        @click="emit('delete', importItem)"
      />
    </div>
  </div>
</template>
